import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { snakeToCamel } from "caseparser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";

import ColumnText from "../molecules/ColumnText";
import { classNames } from "@/shared/utils/classnames";
import { TableHeaderColumn } from "../atoms/TableHeaderColumn";
import { TableLoader } from "../molecules/TableLoader";
import { ChevronDown, ChevronRight } from "lucide-react";

declare global {
  interface Window {
    snakeToCamel: typeof snakeToCamel;
  }
}

window.snakeToCamel = snakeToCamel;

export type AccountingTableProps = {
  accountings: Array<any>;
  className?: string;
  isLoading?: boolean;
  isFetching?: boolean;
  isEnd?: boolean;
  onLazyLoad: () => void;
};

export const AccountingTable = ({
  className,
  accountings,
  isLoading,
  isFetching,
  onLazyLoad,
  isEnd,
}: AccountingTableProps) => {
  const [t] = useTranslation();
  const [showIncomes, setShowIncomes] = useState(true);
  const [showExpected, setShowExpected] = useState(true);
  const [showBills, setShowBills] = useState(true);
  const containerRef = useRef<HTMLDivElement | undefined>();

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        if (
          scrollTop + clientHeight >= scrollHeight - 10 &&
          !isLoading &&
          !isEnd &&
          !isFetching
        ) {
          onLazyLoad();
        }
      }
    };

    const element = containerRef.current;
    element?.addEventListener("scroll", handleScroll);

    return () => {
      element?.removeEventListener("scroll", handleScroll);
    };
  }, [isLoading, onLazyLoad, isFetching]);

  return (
    <div
      className={classNames("flex flex-col flex-1 overflow-scroll border-t")}
      ref={containerRef}
    >
      <div className="flex flex-1 flex-col">
        {isLoading ? (
          <Fragment>
            <TableLoader columns={12} rows={14} />
          </Fragment>
        ) : (
          <Fragment />
        )}
        {accountings.length > 0 && (
          <Fragment>
            <table className="divide-y divide-gray-300 relative">
              <thead
                className="bg-white border-b"
                style={{ position: "sticky", top: 0, left: 0, right: 0 }}
              >
                <tr>
                  <TableHeaderColumn>
                    <div className="flex flex-col">
                      <span>{t("general.date")}</span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className="cursor-pointer"
                    onClick={() => setShowIncomes(!showIncomes)}
                  >
                    <div className="flex justify-between">
                      <div className="flex flex-col w-100">
                        <span>{t("pages.accounting.incomes")}</span>
                        <small className="text-gray-500 font-light">
                          ({t("general.total")})
                        </small>
                      </div>
                      <div className="flex align-items-center justify-content-center">
                        {showIncomes ? <ChevronRight /> : <ChevronDown />}
                      </div>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.debit")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.automatic")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.card")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.automatic")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.cash")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.dataphone")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.transfer")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.simple")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.other")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showIncomes ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>Convenios</span>
                    </div>
                  </TableHeaderColumn>

                  <TableHeaderColumn
                    className="cursor-pointer"
                    onClick={() => setShowExpected(!showExpected)}
                  >
                    <div className="flex justify-between">
                      <div className="flex flex-col w-100">
                        <span>Esperado</span>
                        <small className="text-gray-500 font-light">
                          (total)
                        </small>
                      </div>

                      <div className="flex align-items-center justify-content-center">
                        {showExpected ? <ChevronRight /> : <ChevronDown />}
                      </div>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showExpected ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.debit")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.automatic")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showExpected ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.card")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.automatic")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showExpected ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.cash")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showExpected ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.dataphone")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showExpected ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.transfer")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showExpected ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.simple")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showExpected ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.other")}</span>
                      <small className="text-gray-500 font-light">
                        ({t("pages.accounting.manual")})
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(
                      "px-3 py-3.5 text-left text-sm font-semibold text-gray-900 whitespace-nowrap",
                      showExpected ? "" : "hidden"
                    )}
                  >
                    <div className="flex flex-col">
                      <small className="text-gray-500 font-light">
                        {t("pages.accounting.expected_difference")}
                      </small>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className="cursor-pointer "
                    onClick={() => setShowBills(!showBills)}
                  >
                    <div className="flex justify-between">
                      <div className="flex flex-col w-100">
                        <span>{t("pages.accounting.bills")}</span>
                        <small className="text-gray-500 font-light">
                          ({t("general.total")})
                        </small>
                      </div>
                      <div className="flex align-items-center justify-content-center">
                        {showBills ? <ChevronRight /> : <ChevronDown />}
                      </div>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.categories.staff")}</span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>
                        {t(
                          "pages.accounting.categories.equipmentAndMaintenance"
                        )}
                      </span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.categories.facilities")}</span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>
                        {t("pages.accounting.categories.administrative")}
                      </span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>
                        {t("pages.accounting.categories.marketingAndPublicity")}
                      </span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>
                        {t("pages.accounting.categories.operational")}
                      </span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>
                        {t("pages.accounting.categories.taxesAndLicenses")}
                      </span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>
                        {t("pages.accounting.categories.healthAndSafety")}
                      </span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>
                        {t(
                          "pages.accounting.categories.technologyAndInnovation"
                        )}
                      </span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    className={classNames(showBills ? "" : "hidden")}
                  >
                    <div className="flex flex-col align-items-end">
                      <span>{t("pages.accounting.categories.others")}</span>
                    </div>
                  </TableHeaderColumn>
                  <TableHeaderColumn className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 ">
                    <div className="flex flex-col">
                      <span>{t("pages.accounting.revenue")}</span>
                    </div>
                  </TableHeaderColumn>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {accountings.map((accounting: any) => (
                  <tr>
                    <ColumnText title={accounting.date} className="" />
                    <ColumnText title={accounting.total} className="" />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.automaticDebit}
                    />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.automaticCard}
                    />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.manualCash}
                    />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.manualDataphone}
                    />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.manualTransfer}
                    />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.manualSimple}
                    />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.manualOther}
                    />
                    <ColumnText
                      className={showIncomes ? "" : "hidden"}
                      title={accounting.agreements}
                    />
                    <ColumnText title={accounting.expectedTotal} className="" />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedAutomaticDebit}
                    />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedAutomaticCard}
                    />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedManualCash}
                    />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedManualDataphone}
                    />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedManualTransfer}
                    />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedManualSimple}
                    />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedManualOther}
                    />
                    <ColumnText
                      className={showExpected ? "" : "hidden"}
                      title={accounting.expectedDifference}
                    />
                    <ColumnText title={accounting.billsTotal} className="" />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category1}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category2}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category3}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category4}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category5}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category6}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category7}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category8}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category9}
                    />
                    <ColumnText
                      className={showBills ? "" : "hidden"}
                      title={accounting.category10}
                    />
                    <ColumnText title={accounting.revenue} className="" />
                  </tr>
                ))}
              </tbody>
            </table>
            {isEnd && (
              <div
                className="bg-white flex justify-center items-center flex-1"
                style={{ minHeight: "4rem" }}
              ></div>
            )}
            {isLoading && (
              <div className="bg-white flex justify-center items-center flex-1 pt-2 pb-10 h-24 w-24 mx-auto">
                <FontAwesomeIcon icon={faLoader} spin className="h-6 w-6" />
              </div>
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};
